import * as styles from "../../styles";
import SearchableList from "../SearchableList";
import {
  ArrowDownward as SortDescIcon,
  ArrowUpward as SortAscIcon,
  BarChart as MetricsIcon,
  Check as CheckIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Box,
  Button as MuiButton,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DATE_AGGREGATE_METHODS,
  NUMERIC_AGGREGATE_METHODS,
} from "components/reportsV3/types/constants";
import React from "react";
import { useCallback, useMemo, useRef, useState } from "react";

const MetricsSubMenu = ({
  anchorEl,
  methods,
  activeMethods,
  onToggle,
  onClose,
}) => {
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      disableRestoreFocus
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: { sx: { minWidth: 120 } },
      }}
    >
      <MenuList dense>
        {methods.map((method) => {
          const isActive = activeMethods?.has(method);
          return (
            <MenuItem key={method} onClick={() => onToggle(method)}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {isActive ? (
                  <CheckIcon fontSize={"small"} color={"primary"} />
                ) : null}
              </ListItemIcon>
              <ListItemText
                primary={method}
                slotProps={{
                  primary: {
                    sx: { fontSize: "0.875rem !important", fontWeight: 500 },
                  },
                }}
              />
            </MenuItem>
          );
        })}
      </MenuList>
    </Popover>
  );
};

const MetricsToggle = ({
  columnName,
  columnType,
  isInMetrics,
  activeMethods,
  onAddToMetrics,
  onRemoveFromMetrics,
}) => {
  const theme = useTheme();
  const [subMenuAnchor, setSubMenuAnchor] = useState(null);

  const isNumeric = columnType === "INT" || columnType === "FLOAT";
  const isDate = columnType === "DATE";
  const hasSubMenu = isNumeric || isDate;
  const subMenuMethods = isNumeric
    ? NUMERIC_AGGREGATE_METHODS
    : DATE_AGGREGATE_METHODS;

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (hasSubMenu) {
        setSubMenuAnchor((prev) => (prev ? null : e.currentTarget));
      } else if (isInMetrics) {
        onRemoveFromMetrics(columnName);
      } else {
        onAddToMetrics(columnName);
      }
    },
    [hasSubMenu, isInMetrics, columnName, onAddToMetrics, onRemoveFromMetrics]
  );

  const handleToggleMethod = useCallback(
    (method) => {
      if (activeMethods?.has(method)) {
        onRemoveFromMetrics(columnName, method);
      } else {
        onAddToMetrics(columnName, method);
      }
    },
    [columnName, activeMethods, onAddToMetrics, onRemoveFromMetrics]
  );

  return (
    <React.Fragment>
      <IconButton
        size={"small"}
        onClick={handleClick}
        sx={{ p: 0.25, flexShrink: 0 }}
        title={"Metrics"}
      >
        <MetricsIcon
          fontSize={"small"}
          sx={{
            color: isInMetrics
              ? theme.brand.accent[500]
              : theme.brand.gray[800],
          }}
        />
      </IconButton>
      {hasSubMenu && (
        <MetricsSubMenu
          anchorEl={subMenuAnchor}
          methods={subMenuMethods}
          activeMethods={activeMethods}
          onToggle={handleToggleMethod}
          onClose={() => setSubMenuAnchor(null)}
        />
      )}
    </React.Fragment>
  );
};

const SortIndicator = ({ direction, index, count }) => {
  const Icon = direction === "asc" ? SortAscIcon : SortDescIcon;
  return (
    <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
      <Icon sx={{ fontSize: "1rem", opacity: 0.7, color: "primary.main" }} />
      {count > 1 && index != null && (
        <Box
          component={"span"}
          sx={{
            fontSize: "0.625rem",
            fontWeight: 700,
            lineHeight: 1,
            color: "primary.main",
            opacity: 0.7,
            ml: -0.3,
          }}
        >
          {index + 1}
        </Box>
      )}
    </Box>
  );
};

export const ColumnsButton = ({
  availableColumns,
  showingColumns,
  disabledColumns,
  searchQuery,
  onClose,
  onSearchChange,
  container,
  isMultiTable = false,
  tableDisplayNameMap = {},
  columnDefMap = {},
  metricsColumnMethodMap = {},
  sortModel = [],
  onAddToMetrics,
  onRemoveFromMetrics,
  onReorderColumn,
  iconOnly = false,
}) => {
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [localVisible, setLocalVisible] = useState(
    () => new Set(showingColumns.map((c) => c.internalName))
  );
  const handleToggle = () => {
    if (!open) {
      setLocalVisible(new Set(showingColumns.map((c) => c.internalName)));
    }
    setOpen((prev) => !prev);
  };
  const handleClose = () => {
    onClose(localVisible);
    setOpen(false);
  };
  const handleItemClick = (id) => {
    if (disabledColumns?.has(id)) return;
    setLocalVisible((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const originalVisible = useMemo(
    () => new Set(showingColumns.map((c) => c.internalName)),
    [showingColumns]
  );

  const ambiguousNames = useMemo(() => {
    if (!isMultiTable) return new Set();
    const counts = {};
    availableColumns.forEach((col) => {
      const name = col.displayName || col.internalName;
      counts[name] = (counts[name] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter((k) => counts[k] > 1));
  }, [availableColumns, isMultiTable]);

  const items = useMemo(
    () =>
      availableColumns.map((col) => {
        const id = col.internalName;
        const isDisabled = disabledColumns?.has(id);
        const wasVisible = originalVisible.has(id);
        const isVisible = localVisible.has(id);

        let status;
        if (!isDisabled && wasVisible !== isVisible) {
          status = isVisible ? "added" : "removed";
        }
        const baseName = col.displayName || col.internalName;
        let label = baseName;
        if (isMultiTable && ambiguousNames.has(baseName) && col.sourceTable) {
          const tableName =
            tableDisplayNameMap[col.sourceTable] ?? col.sourceTable;
          label = `${baseName} - ${tableName}`;
        }

        const colDef = columnDefMap[id];
        const columnType = colDef?._type ?? null;
        const activeMethods = metricsColumnMethodMap[id] ?? null;
        const isInMetrics = Boolean(activeMethods);

        const sortIndex = sortModel.findIndex((s) => s.field === id);
        const sortItem = sortIndex >= 0 ? sortModel[sortIndex] : null;
        const isSorted = sortItem !== null;
        const sortDirection = sortItem?.sort ?? null;
        const sortCount = sortModel.length;

        const endAdornment =
          isSorted || (onAddToMetrics && onRemoveFromMetrics) ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {isSorted && (
                <SortIndicator
                  direction={sortDirection}
                  index={sortIndex}
                  count={sortCount}
                />
              )}
              {onAddToMetrics && onRemoveFromMetrics && (
                <MetricsToggle
                  columnName={id}
                  columnType={columnType}
                  isInMetrics={isInMetrics}
                  activeMethods={activeMethods}
                  onAddToMetrics={onAddToMetrics}
                  onRemoveFromMetrics={onRemoveFromMetrics}
                />
              )}
            </Box>
          ) : null;

        return {
          id,
          label,
          selected: isDisabled ? false : isVisible,
          status,
          endAdornment,
          ...(isDisabled && {
            sx: {
              color: "text.disabled",
              backgroundColor: "white",
              cursor: "default",
              "&:hover": { backgroundColor: "white" },
              "&.Mui-selected": { backgroundColor: "white" },
            },
          }),
        };
      }),
    [
      availableColumns,
      localVisible,
      originalVisible,
      isMultiTable,
      ambiguousNames,
      tableDisplayNameMap,
      disabledColumns,
      columnDefMap,
      metricsColumnMethodMap,
      sortModel,
      onAddToMetrics,
      onRemoveFromMetrics,
    ]
  );
  const hasChanges = useMemo(() => {
    if (localVisible.size !== originalVisible.size) return true;
    for (const id of localVisible) {
      if (!originalVisible.has(id)) return true;
    }
    return false;
  }, [localVisible, originalVisible]);
  const handleSave = () => {
    onClose(localVisible);
    setOpen(false);
  };
  const handleCancel = () => {
    setLocalVisible(new Set(originalVisible));
    setOpen(false);
  };
  const actionBar = hasChanges ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 1,
      }}
    >
      <MuiButton
        size={"small"}
        variant={"contained"}
        onClick={handleSave}
        fullWidth
      >
        {"Save"}
      </MuiButton>
      <MuiButton
        size={"small"}
        variant={"contained"}
        color={"secondary"}
        onClick={handleCancel}
        fullWidth
      >
        {"Cancel"}
      </MuiButton>
    </Box>
  ) : null;
  return (
    <React.Fragment>
      {iconOnly ? (
        <Tooltip title={"Columns"}>
          <span>
            <IconButton
              ref={buttonRef}
              size={"small"}
              onClick={handleToggle}
              disabled={availableColumns.length === 0}
              sx={{
                backgroundColor: "grey.100",
                color: "text.primary",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                "&:hover": { backgroundColor: "grey.200" },
              }}
            >
              <EditIcon fontSize={"small"} />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <MuiButton
          variant={"text"}
          size={"small"}
          onClick={handleToggle}
          ref={buttonRef}
          disabled={availableColumns.length === 0}
          endIcon={<EditIcon fontSize={"small"} />}
          sx={{
            backgroundColor: "grey.100",
            color: "text.primary",
            border: "1px solid",
            ...styles.buttonText,
            borderColor: "divider",
            "&:hover": {
              backgroundColor: "grey.200",
            },
            padding: "4px 8px",
          }}
        >
          {"Columns"}
        </MuiButton>
      )}
      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        container={container ?? undefined}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              maxHeight: 480,
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <SearchableList
          items={items}
          onItemClick={(item) => handleItemClick(item.id)}
          isOpen={open}
          searchPlaceholder={"columns"}
          searchValue={searchQuery}
          onSearchChange={onSearchChange}
          emptyMessage={"No columns found"}
          sortAlphabetically={false}
          actionBar={actionBar}
          onReorder={onReorderColumn}
        />
      </Popover>
    </React.Fragment>
  );
};
